import React, { useState } from 'react';
import './RequirementForm.css';

export default function RequirementForm({
  requirement,
  folders,
  onSubmit,
  onCancel,
  loading,
  error,
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    title: requirement?.title || '',
    description: requirement?.description || '',
    type: requirement?.type || 'functional',
    priority: requirement?.priority || 'medium',
    status: requirement?.status || 'draft',
    folderId: requirement ? (requirement.folderId?.id || requirement.folderId?._id || requirement.folderId || '') : '',
    tags: requirement ? (Array.isArray(requirement.tags) ? requirement.tags.join(', ') : '') : '',
  });

  const [formErrors, setFormErrors] = useState({});

  const validateStep = (stepNum) => {
    const errors = {};
    if (stepNum === 1) {
      if (!formData.title.trim()) {
        errors.title = 'Title is required';
      } else if (/[\x00-\x1F\x7F]/.test(formData.title)) {
        errors.title = 'Title must not contain control characters';
      }
    }
    return errors;
  };

  const handleNextStep = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length === 0) {
      setFormErrors({});
      setCurrentStep(currentStep + 1);
    } else {
      setFormErrors(errors);
    }
  };

  const handlePrevStep = () => {
    setFormErrors({});
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = e => {
    e.preventDefault();
    const errors = validateStep(1);
    if (Object.keys(errors).length === 0) {
      onSubmit({
        ...formData,
        title: formData.title.trim().substring(0, 200),
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
    } else {
      setFormErrors(errors);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const tagsArray = formData.tags
    .split(',')
    .map(t => t.trim())
    .filter(Boolean);

  const totalSteps = 4;

  return (
    <div className="rf-page-container">
      {/* Breadcrumb / Navigation */}
      <div className="rf-breadcrumb">
        <button className="rf-back-btn" onClick={onCancel}>
          ← Back
        </button>
        <div className="rf-breadcrumb-text">
          Requirements / <span className="fw-semibold">{requirement ? 'Edit' : 'Create'}</span>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="rf-step-indicator">
        <div className="rf-steps-progress">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className={`rf-step ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}>
              <div className="rf-step-circle">{step}</div>
              <div className="rf-step-label">
                {step === 1 && 'Basic Info'}
                {step === 2 && 'Classification'}
                {step === 3 && 'Organization'}
                {step === 4 && 'Review'}
              </div>
            </div>
          ))}
        </div>
        <div className="rf-progress-bar">
          <div className="rf-progress-fill" style={{ width: `${(currentStep / totalSteps) * 100}%` }}></div>
        </div>
      </div>

      {/* Main Content */}
      <div className="rf-main-content">
        <form onSubmit={handleSubmit} className="rf-form">
          {/* Error Alert */}
          {error && (
            <div className="alert alert-danger mb-4">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="rf-step-content">
              <h2 className="rf-content-title">Basic Information</h2>
              <p className="rf-content-subtitle">Define the title and description for your requirement</p>

              <div className="rf-section">
                <div className="rf-section-header">
                  <h3 className="rf-section-title">Required Fields</h3>
                </div>
                <div className="rf-section-content">
                  <div className="rf-field-group">
                    <label className="rf-field-label">Title *</label>
                    <input
                      type="text"
                      className={`rf-field-input ${
                        formErrors.title ? 'is-invalid' : ''
                      }`}
                      maxLength="200"
                      value={formData.title}
                      onChange={e => handleInputChange('title', e.target.value)}
                      placeholder="e.g., User Authentication System"
                    />
                    <small className="form-text text-muted d-block mt-1">
                      Max 200 characters. Letters, numbers, spaces, and common punctuation (- / &amp; . , etc.) are allowed.
                    </small>
                    {formData.title.length >= 180 && (
                      <small className="text-warning d-block">
                        Approaching character limit ({formData.title.length}/200)
                      </small>
                    )}
                    {formErrors.title && (
                      <small className="text-danger">{formErrors.title}</small>
                    )}
                  </div>
                </div>
              </div>

              <div className="rf-section">
                <div className="rf-section-header">
                  <h3 className="rf-section-title">Optional Details</h3>
                </div>
                <div className="rf-section-content">
                  <div className="rf-field-group">
                    <label className="rf-field-label">Description</label>
                    <textarea
                      className="rf-field-input"
                      rows="6"
                      value={formData.description}
                      onChange={e =>
                        handleInputChange('description', e.target.value)
                      }
                      placeholder="Provide a detailed description of this requirement..."
                    />
                    <small className="rf-form-hint">
                      {formData.description.length} characters
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Classification */}
          {currentStep === 2 && (
            <div className="rf-step-content">
              <h2 className="rf-content-title">Classification</h2>
              <p className="rf-content-subtitle">Categorize your requirement by type and priority</p>

              <div className="rf-section">
                <div className="rf-section-header">
                  <h3 className="rf-section-title">Type & Priority</h3>
                </div>
                <div className="rf-section-content">
                  <div className="rf-fields-row">
                    <div className="rf-field-group">
                      <label className="rf-field-label">Type</label>
                      <select
                        className="rf-field-input"
                        value={formData.type}
                        onChange={e => handleInputChange('type', e.target.value)}
                      >
                        <option value="functional">Functional</option>
                        <option value="non-functional">Non-Functional</option>
                        <option value="business">Business</option>
                        <option value="technical">Technical</option>
                        <option value="other">Other</option>
                      </select>
                      <small className="rf-form-hint">Select the type of requirement</small>
                    </div>

                    <div className="rf-field-group">
                      <label className="rf-field-label">Priority</label>
                      <select
                        className="rf-field-input"
                        value={formData.priority}
                        onChange={e => handleInputChange('priority', e.target.value)}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                      <small className="rf-form-hint">Indicate the priority level</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Organization */}
          {currentStep === 3 && (
            <div className="rf-step-content">
              <h2 className="rf-content-title">Organization</h2>
              <p className="rf-content-subtitle">Set status, folder, and tags for better organization</p>

              <div className="rf-section">
                <div className="rf-section-header">
                  <h3 className="rf-section-title">Status & Folder</h3>
                </div>
                <div className="rf-section-content">
                  <div className="rf-fields-row">
                    <div className="rf-field-group">
                      <label className="rf-field-label">Status</label>
                      <select
                        className="rf-field-input"
                        value={formData.status}
                        onChange={e => handleInputChange('status', e.target.value)}
                      >
                        <option value="draft">Draft</option>
                        <option value="approved">Approved</option>
                        <option value="implemented">Implemented</option>
                        <option value="verified">Verified</option>
                        <option value="deprecated">Deprecated</option>
                      </select>
                    </div>

                    <div className="rf-field-group">
                      <label className="rf-field-label">Folder</label>
                      <select
                        className="rf-field-input"
                        value={formData.folderId}
                        onChange={e => handleInputChange('folderId', e.target.value)}
                      >
                        <option value="">None</option>
                        {folders && folders.map(folder => (
                          <option
                            key={folder.id || folder._id}
                            value={folder.id || folder._id}
                          >
                            {folder.path || ''}{folder.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rf-section">
                <div className="rf-section-header">
                  <h3 className="rf-section-title">Tags</h3>
                  {tagsArray.length > 0 && (
                    <span className="rf-tag-count">{tagsArray.length} tags</span>
                  )}
                </div>
                <div className="rf-section-content">
                  <div className="rf-field-group">
                    <label className="rf-field-label">Tags (comma-separated)</label>
                    <textarea
                      className="rf-field-input"
                      rows="4"
                      value={formData.tags}
                      onChange={e => handleInputChange('tags', e.target.value)}
                      placeholder="e.g., authentication, security, user-management"
                    />
                    <small className="rf-form-hint">
                      Total: {tagsArray.length} tag(s)
                    </small>
                  </div>

                  {/* Tags Preview */}
                  {tagsArray.length > 0 && (
                    <div className="rf-tags-preview">
                      <h5 className="rf-preview-title">Tags Preview</h5>
                      <div className="rf-tags-list">
                        {tagsArray.map((tag, idx) => (
                          <span key={idx} className="rf-tag-badge">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="rf-step-content">
              <h2 className="rf-content-title">Review & Confirm</h2>
              <p className="rf-content-subtitle">Please review your requirement before submitting</p>

              <div className="rf-review-container">
                <div className="rf-review-section">
                  <h5 className="rf-review-heading">Title</h5>
                  <p className="rf-review-text">
                    {formData.title || <em>No title provided</em>}
                  </p>
                </div>

                <div className="rf-review-section">
                  <h5 className="rf-review-heading">Description</h5>
                  <p className="rf-review-text">
                    {formData.description || <em>No description provided</em>}
                  </p>
                </div>

                <div className="rf-review-section">
                  <h5 className="rf-review-heading">Classification</h5>
                  <p className="rf-review-text">
                    <strong>Type:</strong> {formData.type} | <strong>Priority:</strong> {formData.priority}
                  </p>
                </div>

                <div className="rf-review-section">
                  <h5 className="rf-review-heading">Organization</h5>
                  <p className="rf-review-text">
                    <strong>Status:</strong> {formData.status}
                    {formData.folderId && (
                      <>
                        <br />
                        <strong>Folder:</strong>{' '}
                        {folders && folders.find(f => (f.id || f._id) === formData.folderId)?.name || 'Unknown'}
                      </>
                    )}
                  </p>
                </div>

                {tagsArray.length > 0 && (
                  <div className="rf-review-section">
                    <h5 className="rf-review-heading">Tags ({tagsArray.length})</h5>
                    <div className="rf-tags-list">
                      {tagsArray.map((tag, idx) => (
                        <span key={idx} className="rf-tag-badge">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Footer Actions */}
      <div className="rf-footer-bar">
        <button
          type="button"
          className="rf-footer-btn btn-secondary"
          onClick={currentStep === 1 ? onCancel : handlePrevStep}
          disabled={loading}
        >
          {currentStep === 1 ? 'Cancel' : '← Previous'}
        </button>

        {currentStep < totalSteps && (
          <button
            type="button"
            className="rf-footer-btn btn-primary"
            onClick={handleNextStep}
            disabled={loading}
          >
            Next →
          </button>
        )}

        {currentStep === totalSteps && (
          <button
            type="submit"
            className="rf-footer-btn btn-submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                {requirement ? 'Updating...' : 'Creating...'}
              </>
            ) : requirement ? (
              'Update Requirement'
            ) : (
              'Create Requirement'
            )}
          </button>
        )}
      </div>
    </div>
  );
}
